import { CSSProperties, FC, useMemo } from 'react';

import classNames from 'classnames';

import './Spinner.scss';

import LottiePlayer from '../LottiePlayer';
import {
  AnimationOptions,
  LocationOptions,
  SizeOptions,
  TestMetadata,
} from '../types';
import { getSpinnerJSON } from './Spinner.utils';

export interface SpinnerProps {
  /** Add a class to the spinner */
  className?: string;
  /** Loading text for screen readers */
  loadText?: string;
  /** Size: smaller, small, medium (default), large */
  size?: SizeOptions;
  /** Full URL to a custom image you would like to use */
  imageUrl?: string;
  /** Animation: spin (default), bounce, rock, wobble */
  animation?: AnimationOptions;
  /** Location: mask, background */
  /** Mask can break some animations but allows you to change the color of the icon. Use background if Mask is causing problems. */
  location?: LocationOptions;
  /** Test IDs for component sections */
  testMetadata?: TestMetadata;
  /** Type of spinner to use (native or lottie) */
  spinnerType?: 'lottie' | 'native';
}

const Spinner: FC<SpinnerProps> = ({
  className,
  loadText = 'Loading...',
  size = 'medium',
  imageUrl,
  animation = 'spin',
  location = 'mask',
  testMetadata,
  spinnerType = 'native',
  ...otherProps
}) => {
  const spinnerClassnames = useMemo(
    () =>
      classNames(
        `sb-spinner_${location}`,
        `sb-spinner_animate_${animation}`,
        `sb-spinner_${size}`,
      ),
    [size, animation, location],
  );

  let styles = {};
  if (imageUrl) {
    if (location === 'mask') {
      styles = {
        maskImage: `url(${imageUrl})`,
        WebkitMaskImage: `url(${imageUrl})`,
      };
    } else {
      styles = {
        backgroundImage: `url(${imageUrl})`,
      };
    }
  }

  if (spinnerType === 'lottie') {
    let styleSettings: CSSProperties;

    switch (size) {
      case 'large':
        styleSettings = {
          left: 'calc(50% - 40px)',
          maxHeight: '158px',
          maxWidth: '180px',
          position: 'absolute',
        };
        break;
      case 'small':
        styleSettings = {
          left: 'calc(50% - 12px)',
          maxHeight: '24px',
          maxWidth: '24px',
          position: 'absolute',
        };
        break;
      case 'smaller':
        styleSettings = {
          left: 'calc(50% - 8px)',
          maxHeight: '16px',
          maxWidth: '16px',
          position: 'absolute',
        };
        break;
      default:
        styleSettings = {
          left: 'calc(50% - 20px)',
          maxHeight: '40px',
          maxWidth: '40px',
          position: 'absolute',
        };
    }

    return (
      <LottiePlayer
        customOptions={{
          animationData: getSpinnerJSON(size),
        }}
        style={styleSettings}
        testMetadata={testMetadata}
      />
    );
  }

  return (
    <div
      {...otherProps}
      className={classNames('sb-spinner', className)}
      role="alert"
      aria-live="assertive"
      {...testMetadata}
    >
      <p className="sb-spinner-load-text">{loadText}</p>
      <div
        className={spinnerClassnames}
        style={styles}
        data-testid="spinner-mask"
      />
    </div>
  );
};

export default Spinner;
